import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const EventDashboard = ({ eventId: propEventId }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filter, setFilter] = useState('all'); // all, paid, unpaid, checked_in
  const [searchQuery, setSearchQuery] = useState('');
  const [checkingIn, setCheckingIn] = useState(null);
  const [undoingCheckIn, setUndoingCheckIn] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [toast, setToast] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', phone: '' });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [memberAddingId, setMemberAddingId] = useState(null);
  const [cancellingUserId, setCancellingUserId] = useState(null);
  const [phonePromptFor, setPhonePromptFor] = useState(null); // { userId, userName, currentPhone }
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [paymentPromptFor, setPaymentPromptFor] = useState(null); // { userId, userName, amount }
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);

  const getEventId = () => {
    if (propEventId) return propEventId;
    const urlPath = window.location.pathname;
    const match = urlPath.match(/\/events\/(\d+)/);
    return match ? match[1] : null;
  };

  const eventId = getEventId();

  useEffect(() => {
    if (eventId) {
      loadDashboard();
      loadQrCode();
    }
  }, [eventId]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/events/${eventId}/dashboard`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      if (data.success) {
        setDashboard(data);
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQrCode = async () => {
    try {
      const response = await fetch(`/api/v1/events/${eventId}/qr_code`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });
      const data = await response.json();
      if (data.success && data.qr_code_token) {
        setQrToken(data.qr_code_token);
      }
    } catch (error) {
      console.error('Erreur chargement QR code:', error);
    }
  };

  const handleCheckIn = async (userId, userName) => {
    setCheckingIn(userId);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/check_in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`✅ ${userName} marqué(e) présent(e)`);
        loadDashboard();
      } else {
        showToast(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showToast('❌ Erreur lors du check-in', 'error');
    } finally {
      setCheckingIn(null);
    }
  };

  const handleUndoCheckIn = async (userId, userName) => {
    setUndoingCheckIn(userId);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/undo_check_in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`↩️ Émargement de ${userName} annulé`);
        loadDashboard();
      } else {
        showToast(`❌ ${data.message}`, 'error');
      }
    } catch {
      showToast('❌ Erreur réseau', 'error');
    } finally {
      setUndoingCheckIn(null);
    }
  };

  const handleCancelRegistration = async (userId, userName) => {
    if (!confirm(`Êtes-vous sûr de vouloir annuler l'inscription de ${userName} ?`)) return;
    setCancellingUserId(userId);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/cancel_registration`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`🚫 Inscription de ${userName} annulée`);
        loadDashboard();
      } else {
        showToast(`❌ ${data.message || data.error}`, 'error');
      }
    } catch (error) {
      showToast('❌ Erreur réseau', 'error');
    } finally {
      setCancellingUserId(null);
    }
  };

  const openPhonePrompt = (userId, userName, currentPhone) => {
    setPhonePromptFor({ userId, userName, currentPhone });
    setPhoneInput(currentPhone || '');
  };

  const submitPhone = async () => {
    if (!phonePromptFor) return;
    const phone = phoneInput.trim();
    if (!phone) { showToast('❌ Numéro requis', 'error'); return; }
    setPhoneSaving(true);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/set_participant_phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
        body: JSON.stringify({ user_id: phonePromptFor.userId, phone })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`📱 Téléphone enregistré pour ${phonePromptFor.userName}`);
        setPhonePromptFor(null);
        setPhoneInput('');
        loadDashboard();
      } else {
        showToast(`❌ ${data.message}`, 'error');
      }
    } catch {
      showToast('❌ Erreur réseau', 'error');
    } finally {
      setPhoneSaving(false);
    }
  };

  const openPaymentPrompt = (userId, userName, amount) => {
    setPaymentPromptFor({ userId, userName, amount });
    setPaymentMethod('cash');
    setPaymentNote('');
  };

  const submitManualPayment = async () => {
    if (!paymentPromptFor) return;
    setPaymentSaving(true);
    try {
      const response = await fetch(`/api/v1/events/${eventId}/mark_as_paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
        body: JSON.stringify({ user_id: paymentPromptFor.userId, payment_method: paymentMethod, note: paymentNote })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`💶 Paiement validé pour ${paymentPromptFor.userName}`);
        setPaymentPromptFor(null);
        loadDashboard();
      } else {
        showToast(`❌ ${data.message}`, 'error');
      }
    } catch {
      showToast('❌ Erreur réseau', 'error');
    } finally {
      setPaymentSaving(false);
    }
  };

  const exportCSV = () => {
    if (!dashboard) return;
    const questionnaireFields = dashboard.questionnaire_fields || [];
    const rows = [
      [
        'Nom', 'Email', 'Niveau', 'Statut', 'Montant payé', 'Payé', 'Émargé', 'Date inscription',
        ...questionnaireFields.map(field => field.label)
      ],
      ...dashboard.participants.map(p => [
        p.user.name,
        p.user.email,
        p.user.level,
        p.status,
        `${p.payment_amount}€`,
        p.paid ? 'Oui' : 'Non',
        p.checked_in ? 'Oui' : 'Non',
        new Date(p.created_at).toLocaleString('fr-FR'),
        ...questionnaireFields.map(field => formatCustomValue(field, p.custom_data?.[field.name]))
      ])
    ];
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscriptions_evenement_${eventId}.csv`;
    link.click();
  };

  const getFilteredParticipants = () => {
    if (!dashboard) return [];
    return dashboard.participants.filter(p => {
      const matchesFilter =
        filter === 'all' ? true :
        filter === 'paid' ? p.paid :
        filter === 'unpaid' ? !p.paid :
        filter === 'checked_in' ? p.checked_in :
        filter === 'waitlist' ? p.status === 'waitlist' : true;

      const matchesSearch = searchQuery === '' ||
        p.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.user.email.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesFilter && matchesSearch;
    });
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  const formatCustomValue = (field, value) => {
    if (value == null || value === '') return '—';
    if (Array.isArray(value)) return value.join(', ');

    if (field?.type === 'select' && Array.isArray(field.options)) {
      const match = field.options.find(option => option.value === value);
      return match?.label || value;
    }

    return String(value);
  };

  const questionnaireFields = dashboard?.questionnaire_fields || [];

  const checkinUrl = qrToken
    ? `${window.location.origin}/events/${eventId}/checkin?token=${qrToken}`
    : `${window.location.origin}/events/${eventId}/checkin`;

  if (loading) {
    return (
      <div style={{ padding: '160px 40px 40px', minHeight: '100vh', background: '#f7eef2' }}>
        <div style={{ color: '#2c0e16', textAlign: 'center', fontSize: '1.2rem' }}>Chargement...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div style={{ padding: '160px 40px 40px', minHeight: '100vh', background: '#f7eef2' }}>
        <div style={{ color: '#2c0e16', textAlign: 'center' }}>Erreur de chargement</div>
      </div>
    );
  }

  const { statistics: stats, participants } = dashboard;
  const filteredParticipants = getFilteredParticipants();

  const tabStyle = (tab) => ({
    background: activeTab === tab ? 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)' : 'transparent',
    border: activeTab === tab ? 'none' : '1px solid rgba(124,40,55,0.2)',
    color: activeTab === tab ? '#fff' : '#2c0e16',
    padding: '10px 22px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '600',
    transition: 'all 0.2s ease'
  });

  const filterBtnStyle = (val) => ({
    background: filter === val ? 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)' : '#fdf6f8',
    border: filter === val ? 'none' : '1px solid rgba(124,40,55,0.2)',
    color: filter === val ? '#fff' : '#2c0e16',
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: '600',
    transition: 'all 0.2s ease'
  });

  return (
    <div className="event-mobile-shell event-dashboard-page" suppressHydrationWarning style={{ padding: '160px 40px 40px', minHeight: '100vh', background: '#f7eef2', fontFamily: 'inherit' }}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? '#7c1c2a' : '#1a3a2a',
          border: `1px solid ${toast.type === 'error' ? '#c0392b' : '#27ae60'}`,
          color: '#fff', padding: '14px 24px', borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', fontSize: '1rem', fontWeight: '600',
          transition: 'all 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Modale: ajouter/modifier téléphone */}
      {phonePromptFor && (
        <div onClick={() => !phoneSaving && setPhonePromptFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 12px', color: '#2c0e16' }}>📱 Numéro de téléphone</h3>
            <p style={{ color: '#7a4050', fontSize: 14, margin: '0 0 16px' }}>
              Pour <strong>{phonePromptFor.userName}</strong>. Format international recommandé (ex. <code>+33612345678</code>).
            </p>
            <input
              type="tel"
              value={phoneInput}
              onChange={e => setPhoneInput(e.target.value)}
              placeholder="+33 6 12 34 56 78"
              autoFocus
              style={{ width: '100%', padding: '10px 12px', border: '1px solid rgba(124,40,55,0.25)', borderRadius: 10, fontSize: 16, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setPhonePromptFor(null)} disabled={phoneSaving} style={{ background: 'transparent', border: '1px solid rgba(124,40,55,0.25)', color: '#7a4050', padding: '10px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
              <button onClick={submitPhone} disabled={phoneSaving} style={{ background: 'linear-gradient(135deg, #7c2837, #a8384e)', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 10, cursor: phoneSaving ? 'wait' : 'pointer', fontWeight: 600 }}>{phoneSaving ? '⏳…' : '💾 Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale: validation paiement manuel */}
      {paymentPromptFor && (
        <div onClick={() => !paymentSaving && setPaymentPromptFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 12px', color: '#2c0e16' }}>💶 Valider un paiement</h3>
            <p style={{ color: '#7a4050', fontSize: 14, margin: '0 0 16px' }}>
              <strong>{paymentPromptFor.userName}</strong> — montant : <strong>{paymentPromptFor.amount}€</strong>
            </p>
            <label style={{ display: 'block', color: '#7a4050', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Mode de paiement</label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid rgba(124,40,55,0.25)', borderRadius: 10, fontSize: 15, marginBottom: 14, boxSizing: 'border-box', background: '#fff' }}>
              <option value="cash">Espèces</option>
              <option value="transfer">Virement</option>
              <option value="check">Chèque</option>
              <option value="free">Gratuité / Offert</option>
              <option value="other">Autre</option>
            </select>
            <label style={{ display: 'block', color: '#7a4050', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Note (optionnelle)</label>
            <input
              type="text"
              value={paymentNote}
              onChange={e => setPaymentNote(e.target.value)}
              placeholder="Ex : reçu en main propre le 12/06"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid rgba(124,40,55,0.25)', borderRadius: 10, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.15)', borderRadius: 10, padding: 12, marginTop: 14, fontSize: 12, color: '#7a4050' }}>
              ⚠️ Cette action crée un paiement marqué <strong>réussi</strong> dans la base, sans transaction Stripe. Un reçu sera généré automatiquement.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setPaymentPromptFor(null)} disabled={paymentSaving} style={{ background: 'transparent', border: '1px solid rgba(124,40,55,0.25)', color: '#7a4050', padding: '10px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
              <button onClick={submitManualPayment} disabled={paymentSaving} style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 10, cursor: paymentSaving ? 'wait' : 'pointer', fontWeight: 600 }}>{paymentSaving ? '⏳…' : '✅ Valider le paiement'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="event-mobile-container event-dashboard-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div className="event-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ color: '#2c0e16', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: '800', margin: 0 }}>
            📊 Dashboard Organisateur
          </h1>
          <div className="event-page-header-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={exportCSV} style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
              📥 Exporter CSV
            </button>
            <a href={`/events/${eventId}/edit`} style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 20px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600' }}>
              ✏️ Modifier l'événement
            </a>
            <a href={`/events/${eventId}/communication`} style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 20px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600' }}>
              💬 Communication
            </a>
            <a href={`/events/${eventId}/organizers`} style={{ background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)', color: '#fff', padding: '10px 20px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600' }}>
              👥 Organisateurs
            </a>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="event-responsive-grid event-dashboard-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {[
            { icon: '🎟️', label: 'Inscriptions', value: stats.total_registrations, sub: `${stats.fill_rate}% remplissage`, color: '#943143', border: 'rgba(124,40,55,0.3)' },
            { icon: '💶', label: 'Revenus', value: `${stats.total_revenue}€`, sub: `${stats.paid_count} payé(s)`, color: '#15803d', border: 'rgba(34,197,94,0.2)' },
            { icon: '✅', label: 'Émargés', value: stats.checked_in_count, sub: `sur ${stats.total_registrations} inscrits`, color: '#3b82f6', border: 'rgba(59,130,246,0.2)' },
            { icon: '⏳', label: 'Liste d\'attente', value: stats.waitlist_count, sub: `${stats.spots_remaining} place(s) restante(s)`, color: '#f59e0b', border: 'rgba(245,158,11,0.2)' }
          ].map(card => (
            <div key={card.label} style={{ background: '#ffffff', borderRadius: '14px', padding: '20px', border: `1px solid ${card.border}` }}>
              <div style={{ color: '#9e6575', fontSize: '0.85rem', marginBottom: '6px' }}>{card.icon} {card.label}</div>
              <div style={{ color: '#2c0e16', fontSize: '2.2rem', fontWeight: '800', lineHeight: 1 }}>{card.value}</div>
              <div style={{ color: card.color, fontSize: '0.8rem', marginTop: '8px' }}>{card.sub}</div>
            </div>
          ))}
        </div>

        {/* Barre de progression présence */}
        <div style={{ background: '#ffffff', borderRadius: '14px', padding: '20px', marginBottom: '24px', border: '1px solid rgba(124,40,55,0.10)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#9e6575', fontSize: '0.85rem' }}>Taux de présence</span>
            <span style={{ color: '#2c0e16', fontWeight: '700', fontSize: '0.9rem' }}>
              {stats.total_registrations > 0 ? Math.round(stats.checked_in_count / stats.total_registrations * 100) : 0}%
            </span>
          </div>
          <div style={{ background: '#fdf6f8', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '6px',
              width: `${stats.total_registrations > 0 ? (stats.checked_in_count / stats.total_registrations * 100) : 0}%`,
              background: 'linear-gradient(90deg, #3b82f6, #22c55e)',
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button style={tabStyle('overview')} onClick={() => setActiveTab('overview')}>👥 Participants</button>
          <button style={tabStyle('checkin')} onClick={() => setActiveTab('checkin')}>✅ Check-in manuel</button>
          <button style={tabStyle('qrcode')} onClick={() => setActiveTab('qrcode')}>📱 QR Code</button>
          <button style={tabStyle('stats')} onClick={() => setActiveTab('stats')}>📊 Statistiques</button>
          <button style={tabStyle('invite')} onClick={() => { setActiveTab('invite'); setInviteResult(null); }}>🙋 Inviter</button>
        </div>

        {/* ===== ONGLET PARTICIPANTS ===== */}
        {activeTab === 'overview' && (
          <div>
            {/* Filtres + Recherche */}
            <div className="event-dashboard-filters" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
              {[
                { value: 'all', label: `Tous (${participants.length})` },
                { value: 'paid', label: `Payés (${stats.paid_count})` },
                { value: 'unpaid', label: `Non payés (${stats.unpaid_count})` },
                { value: 'checked_in', label: `Émargés (${stats.checked_in_count})` },
                { value: 'waitlist', label: `Attente (${stats.waitlist_count})` }
              ].map(f => (
                <button key={f.value} style={filterBtnStyle(f.value)} onClick={() => setFilter(f.value)}>{f.label}</button>
              ))}
              <input
                type="text"
                placeholder="🔍 Rechercher..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ marginLeft: 'auto', background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.20)', color: '#2c0e16', padding: '8px 16px', borderRadius: '8px', outline: 'none', minWidth: '200px' }}
              />
            </div>

            {/* Tableau */}
            <div className="event-dashboard-table" style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid rgba(124,40,55,0.12)', overflow: 'hidden' }}>
              <div className="event-dashboard-table-head" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px 90px', padding: '14px 20px', background: '#f0e4e8', color: '#7a4050', fontWeight: '700', fontSize: '0.82rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                <div>Participant</div><div>Niveau</div><div>Invité par</div><div>Montant</div><div>Paiement</div><div style={{ textAlign: 'center' }}>Émargé</div><div style={{ textAlign: 'center' }}>Action</div>
              </div>
              {filteredParticipants.length > 0 ? filteredParticipants.map(p => {
                const answeredFields = questionnaireFields
                  .map(field => ({ field, value: p.custom_data?.[field.name] }))
                  .filter(({ value }) => value != null && value !== '');

                return (
                  <div key={p.id} style={{ borderBottom: '1px solid rgba(124,40,55,0.10)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,40,55,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div className="event-dashboard-table-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 120px 90px', padding: '16px 20px', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: '#2c0e16', fontWeight: '600', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {p.is_plus_one && (
                            <span style={{ background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', borderRadius: 6, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 800, flexShrink: 0 }}>+1</span>
                          )}
                          {p.user.name}
                        </div>
                        <div style={{ color: '#7a4050', fontSize: '0.82rem' }}>{p.user.email}</div>
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {p.user.phone ? (
                            <span style={{ color: '#15803d', fontSize: '0.78rem', fontWeight: 600 }} title={p.user.phone_verified ? 'Vérifié' : 'Non vérifié'}>
                              📱 {p.user.phone}
                            </span>
                          ) : (
                            <span style={{ color: '#9e6575', fontSize: '0.78rem', fontStyle: 'italic' }}>📱 Aucun numéro</span>
                          )}
                          <button
                            onClick={() => openPhonePrompt(p.user.id, p.user.name, p.user.phone)}
                            style={{ background: 'transparent', border: '1px solid rgba(124,40,55,0.25)', color: '#7c2837', padding: '2px 8px', borderRadius: 6, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}>
                            {p.user.phone ? '✏️ Modifier' : '➕ Ajouter'}
                          </button>
                        </div>
                        {p.is_plus_one && p.plus_one_of && (
                          <div style={{ color: '#9e6575', fontSize: '0.78rem' }}>👤 Invité de {p.plus_one_of.user_name}</div>
                        )}
                        {p.status === 'waitlist' && <div style={{ color: '#f59e0b', fontSize: '0.78rem' }}>⏳ Liste d'attente #{p.waitlist_position}</div>}
                      </div>
                      <div><span style={{ background: '#f0e4e8', padding: '3px 10px', borderRadius: '6px', fontSize: '0.82rem', color: '#7a4050' }}>{p.user.level}</span></div>
                      <div>
                        {p.invited_by ? (
                          <span style={{ color: '#7c2837', fontSize: '0.85rem', fontWeight: '600' }}>👤 {p.invited_by.name}</span>
                        ) : (
                          <span style={{ color: '#9e6575', fontSize: '0.82rem', fontStyle: 'italic' }}>—</span>
                        )}
                      </div>
                      <div style={{ color: '#2c0e16', fontWeight: '600' }}>{p.payment_amount}€</div>
                      <div>
                        {p.paid
                          ? <span style={{ background: 'rgba(34,197,94,0.15)', color: '#15803d', padding: '4px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600' }}>✅ Payé</span>
                          : (
                            <button
                              onClick={() => openPaymentPrompt(p.user.id, p.user.name, p.payment_amount)}
                              title="Valider manuellement le paiement"
                              style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px dashed rgba(245,158,11,0.45)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer' }}>
                              ⏳ Valider…
                            </button>
                          )}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {p.checked_in
                          ? <button
                              onClick={() => handleUndoCheckIn(p.user.id, p.user.name)}
                              disabled={undoingCheckIn === p.user.id}
                              title={p.checked_in_at ? `Émargé le ${formatDate(p.checked_in_at)}` : 'Émargé'}
                              style={{ background: undoingCheckIn === p.user.id ? '#e0ccd4' : '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#7c2837', padding: '5px 12px', borderRadius: '8px', cursor: undoingCheckIn === p.user.id ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.82rem' }}>
                              {undoingCheckIn === p.user.id ? '…' : '↩️ Annuler'}
                            </button>
                          : <span style={{ fontSize: '1.4rem', opacity: 0.15 }}>⬜</span>}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => handleCancelRegistration(p.user.id, p.user.name)}
                          disabled={cancellingUserId === p.user.id}
                          title="Annuler l'inscription"
                          style={{
                            background: cancellingUserId === p.user.id ? '#e0ccd4' : 'transparent',
                            border: '1px solid rgba(220,53,69,0.3)', color: '#dc3545',
                            padding: '5px 10px', borderRadius: '8px',
                            cursor: cancellingUserId === p.user.id ? 'not-allowed' : 'pointer',
                            fontWeight: '600', fontSize: '0.78rem', transition: 'all 0.2s'
                          }}>
                          {cancellingUserId === p.user.id ? '…' : '🚫'}
                        </button>
                      </div>
                    </div>
                    {questionnaireFields.length > 0 && (
                      <div style={{ padding: '0 20px 16px 20px' }}>
                        <div style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.10)', borderRadius: '12px', padding: '14px 16px' }}>
                          <div style={{ color: '#7a4050', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: answeredFields.length > 0 ? '10px' : 0 }}>
                            Questions complémentaires
                          </div>
                          {answeredFields.length > 0 ? (
                            <div className="event-dashboard-answers-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 18px' }}>
                              {answeredFields.map(({ field, value }) => (
                                <div key={`${p.id}-${field.name}`}>
                                  <div style={{ color: '#9e6575', fontSize: '0.78rem', marginBottom: 4 }}>{field.label}</div>
                                  <div style={{ color: '#2c0e16', fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.45 }}>{formatCustomValue(field, value)}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ color: '#9e6575', fontSize: '0.85rem' }}>Aucune réponse enregistrée.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div style={{ padding: '48px', textAlign: 'center', color: '#555' }}>Aucun participant ne correspond</div>
              )}
            </div>
          </div>
        )}

        {/* ===== ONGLET CHECK-IN MANUEL ===== */}
        {activeTab === 'checkin' && (
          <div className="event-dashboard-checkin-table" style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid rgba(124,40,55,0.12)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', background: '#f0e4e8', borderBottom: '1px solid rgba(124,40,55,0.10)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: '#2c0e16', margin: 0, fontWeight: '700' }}>✅ Check-in manuel</h3>
              <div style={{ color: '#7a4050', fontSize: '0.9rem' }}>
                {stats.checked_in_count} / {stats.total_registrations} émargés
              </div>
            </div>

            {/* Barre de recherche */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
              <input
                type="text"
                placeholder="🔍 Rechercher un participant..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ width: '100%', background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.20)', color: '#2c0e16', padding: '10px 16px', borderRadius: '8px', outline: 'none', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
            </div>

            {/* En-tête tableau */}
            <div className="event-dashboard-checkin-head" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 160px', padding: '12px 24px', background: '#f0e4e8', color: '#555', fontWeight: '700', fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              <div>Participant</div><div>Niveau</div><div>Statut</div><div style={{ textAlign: 'center' }}>Action</div>
            </div>

            {/* Lignes */}
            {participants
              .filter(p => p.status === 'registered' && (
                searchQuery === '' ||
                p.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.user.email.toLowerCase().includes(searchQuery.toLowerCase())
              ))
              .sort((a, b) => a.checked_in === b.checked_in ? a.user.name.localeCompare(b.user.name) : a.checked_in ? 1 : -1)
              .map(p => (
                <div key={p.id}
                  className="event-dashboard-checkin-row"
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 160px', padding: '14px 24px', borderBottom: '1px solid rgba(124,40,55,0.10)', alignItems: 'center', background: p.checked_in ? 'rgba(34,197,94,0.04)' : 'transparent', transition: 'background 0.2s' }}
                  onMouseEnter={e => !p.checked_in && (e.currentTarget.style.background = 'rgba(124,40,55,0.04)')}
                  onMouseLeave={e => e.currentTarget.style.background = p.checked_in ? 'rgba(34,197,94,0.04)' : 'transparent'}>
                  <div>
                    <div style={{ color: '#2c0e16', fontWeight: '600' }}>{p.user.name}</div>
                    <div style={{ color: '#555', fontSize: '0.82rem' }}>{p.user.email}</div>
                  </div>
                  <div><span style={{ background: '#f0e4e8', padding: '3px 10px', borderRadius: '6px', fontSize: '0.82rem', color: '#7a4050' }}>{p.user.level}</span></div>
                  <div>
                    {p.checked_in
                      ? <span style={{ color: '#15803d', fontWeight: '600', fontSize: '0.88rem' }}>✅ Présent{p.checked_in_at ? ` · ${formatDate(p.checked_in_at)}` : ''}</span>
                      : <span style={{ color: '#9e6575', fontSize: '0.88rem' }}>En attente</span>}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {p.checked_in ? (
                      <button
                        onClick={() => handleUndoCheckIn(p.user.id, p.user.name)}
                        disabled={undoingCheckIn === p.user.id}
                        style={{
                          background: undoingCheckIn === p.user.id ? '#e0ccd4' : '#fdf6f8',
                          border: '1px solid rgba(124,40,55,0.25)', color: '#7c2837',
                          padding: '8px 14px', borderRadius: '8px',
                          cursor: undoingCheckIn === p.user.id ? 'not-allowed' : 'pointer',
                          fontWeight: '700', fontSize: '0.85rem', transition: 'all 0.2s'
                        }}>
                        {undoingCheckIn === p.user.id ? '…' : '↩️ Annuler'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleCheckIn(p.user.id, p.user.name)}
                        disabled={checkingIn === p.user.id}
                        style={{
                          background: checkingIn === p.user.id ? '#e0ccd4' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                          border: 'none', color: '#fff', padding: '8px 18px', borderRadius: '8px',
                          cursor: checkingIn === p.user.id ? 'not-allowed' : 'pointer',
                          fontWeight: '700', fontSize: '0.88rem', transition: 'all 0.2s'
                        }}>
                        {checkingIn === p.user.id ? '...' : '✅ Marquer présent'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            {participants.filter(p => p.status === 'registered').length === 0 && (
              <div style={{ padding: '48px', textAlign: 'center', color: '#555' }}>Aucun inscrit pour le moment</div>
            )}
          </div>
        )}

        {/* ===== ONGLET QR CODE ===== */}
        {activeTab === 'qrcode' && (
          <div className="event-responsive-grid event-dashboard-qr-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px' }}>
            {/* QR Code événement */}
            <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid rgba(124,40,55,0.12)', padding: '32px', textAlign: 'center' }}>
              <h3 style={{ color: '#2c0e16', marginBottom: '8px', fontWeight: '700' }}>📱 QR Code de l'événement</h3>
              <p style={{ color: '#9e6575', fontSize: '0.88rem', marginBottom: '24px' }}>
                Affichez ce code à l'entrée : les participants le scannent pour s'émarger automatiquement.
              </p>
              <div style={{ display: 'inline-block', background: '#fff', padding: '16px', borderRadius: '12px', boxShadow: '0 0 40px rgba(124,40,55,0.3)' }}>
                {qrToken
                  ? <QRCodeSVG value={checkinUrl} size={220} level="H" />
                  : <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7a4050', fontSize: '0.9rem' }}>Token indisponible</div>}
              </div>
              <div className="event-dashboard-qr-actions" style={{ marginTop: '20px' }}>
                <div style={{ color: '#555', fontSize: '0.75rem', marginBottom: '8px', wordBreak: 'break-all' }}>{checkinUrl}</div>
                <button onClick={() => { navigator.clipboard.writeText(checkinUrl); showToast('🔗 Lien copié !'); }}
                  style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', marginRight: '8px' }}>
                  🔗 Copier le lien
                </button>
                <button onClick={() => window.print()}
                  style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                  🖨️ Imprimer
                </button>
              </div>
            </div>

            {/* Récapitulatif check-in */}
            <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid rgba(124,40,55,0.12)', padding: '32px' }}>
              <h3 style={{ color: '#2c0e16', marginBottom: '20px', fontWeight: '700' }}>📋 État des émargements</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Total inscrits', value: stats.total_registrations, color: '#2c0e16' },
                  { label: 'Présents', value: stats.checked_in_count, color: '#15803d' },
                  { label: 'Absents', value: stats.total_registrations - stats.checked_in_count, color: '#f59e0b' },
                  { label: 'Liste d\'attente', value: stats.waitlist_count, color: '#3b82f6' }
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f0e4e8', borderRadius: '10px' }}>
                    <span style={{ color: '#9e6575' }}>{item.label}</span>
                    <span style={{ color: item.color, fontWeight: '800', fontSize: '1.4rem' }}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#9e6575', fontSize: '0.85rem' }}>Progression</span>
                  <span style={{ color: '#2c0e16', fontWeight: '700' }}>
                    {stats.total_registrations > 0 ? Math.round(stats.checked_in_count / stats.total_registrations * 100) : 0}%
                  </span>
                </div>
                <div style={{ background: '#fdf6f8', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '8px',
                    width: `${stats.total_registrations > 0 ? stats.checked_in_count / stats.total_registrations * 100 : 0}%`,
                    background: 'linear-gradient(90deg,#3b82f6,#22c55e)', transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
              <button onClick={loadDashboard} style={{ marginTop: '20px', width: '100%', background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.20)', color: '#2c0e16', padding: '12px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                🔄 Actualiser
              </button>
            </div>
          </div>
        )}

        {/* ===== ONGLET INVITER ===== */}
        {activeTab === 'invite' && (() => {
          const joinUrl = dashboard?.slug
            ? `${window.location.origin}/e/${dashboard.slug}`
            : `${window.location.origin}/events/${eventId}/join`;
          const inputS = {
            width: '100%', padding: '11px 14px',
            border: '1.5px solid rgba(124,40,55,0.18)', borderRadius: '8px',
            background: '#fdf6f8', color: '#2c0e16', fontSize: '15px',
            boxSizing: 'border-box', outline: 'none',
          };
          const labelS = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#7a4050', marginBottom: '6px' };

          const registeredEmails = new Set((dashboard?.participants || []).map(p => p.user?.email));
          const registeredUserIds = new Set((dashboard?.participants || []).map(p => p.user?.id));

          const searchMembers = async (q) => {
            setMemberSearch(q);
            if (q.length < 2) { setMemberResults([]); return; }
            setMemberSearchLoading(true);
            try {
              const res = await fetch(`/api/v1/users?search=${encodeURIComponent(q)}&per_page=8&status=active`, {
                headers: { 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content }
              });
              const data = await res.json();
              setMemberResults(data.users || data.data?.users || []);
            } catch { setMemberResults([]); } finally { setMemberSearchLoading(false); }
          };

          const addMember = async (user) => {
            setMemberAddingId(user.id);
            try {
              const res = await fetch(`/api/v1/events/${eventId}/invite_guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
                body: JSON.stringify({ email: user.email, first_name: user.first_name, last_name: user.last_name })
              });
              const data = await res.json();
              if (data.success) {
                showToast(`✅ ${data.message}`);
                setMemberResults(prev => prev.filter(u => u.id !== user.id));
                loadDashboard();
              } else {
                showToast(`❌ ${data.message}`, 'error');
              }
            } catch { showToast('❌ Erreur réseau', 'error'); } finally { setMemberAddingId(null); }
          };

          const handleInvite = async (e) => {
            e.preventDefault();
            setInviteSubmitting(true);
            setInviteResult(null);
            try {
              const res = await fetch(`/api/v1/events/${eventId}/invite_guest`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
                },
                body: JSON.stringify(inviteForm)
              });
              const data = await res.json();
              setInviteResult(data);
              if (data.success) {
                setInviteForm({ email: '', first_name: '', last_name: '', phone: '' });
                loadDashboard();
                showToast(`✅ ${data.message}`);
              } else {
                showToast(`❌ ${data.message}`, 'error');
              }
            } catch {
              showToast('❌ Erreur réseau', 'error');
            } finally {
              setInviteSubmitting(false);
            }
          };

          return (
            <div className="event-responsive-grid event-dashboard-invite-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
              {/* Rechercher un membre existant — dropdown autocomplete */}
              <div style={{ background: '#ffffff', borderRadius: '16px', border: '2px solid rgba(124,40,55,0.25)', padding: '28px' }}>
                <h3 style={{ color: '#2c0e16', marginBottom: '8px', fontWeight: '700' }}>🔍 Ajouter un membre existant</h3>
                <p style={{ color: '#7a4050', fontSize: '14px', marginBottom: '16px' }}>
                  Tapez un nom ou email pour rechercher parmi les membres.
                </p>
                <div style={{ position: 'relative' }}>
                  <input
                    value={memberSearch}
                    onChange={e => searchMembers(e.target.value)}
                    onFocus={e => { if (e.target.value.length >= 2) searchMembers(e.target.value); }}
                    onBlur={() => setTimeout(() => setMemberResults([]), 200)}
                    placeholder="Rechercher un membre…"
                    autoComplete="off"
                    style={{ ...inputS, paddingRight: memberSearchLoading ? '40px' : '14px' }}
                  />
                  {memberSearchLoading && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9e6575', fontSize: '13px' }}>⏳</span>
                  )}
                  {memberSearch.length >= 2 && !memberSearchLoading && memberResults.length === 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid rgba(124,40,55,0.18)', borderRadius: '0 0 10px 10px', padding: '12px 16px', fontSize: '13px', color: '#9e6575', boxShadow: '0 6px 20px rgba(44,14,22,0.10)' }}>
                      Aucun membre trouvé pour « {memberSearch} »
                    </div>
                  )}
                  {memberResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid rgba(124,40,55,0.20)', borderRadius: '0 0 12px 12px', boxShadow: '0 8px 24px rgba(44,14,22,0.13)', overflow: 'hidden', maxHeight: '280px', overflowY: 'auto' }}>
                      {memberResults.map((u, idx) => {
                        const alreadyIn = registeredEmails.has(u.email) || registeredUserIds.has(u.id);
                        return (
                          <div
                            key={u.id}
                            onMouseDown={e => e.preventDefault()}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 16px', borderBottom: idx < memberResults.length - 1 ? '1px solid rgba(124,40,55,0.07)' : 'none', background: alreadyIn ? '#fdf6f8' : '#fff', transition: 'background 0.12s' }}
                            onMouseEnter={e => { if (!alreadyIn) e.currentTarget.style.background = '#faf0f3'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = alreadyIn ? '#fdf6f8' : '#fff'; }}
                          >
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#2c0e16', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.full_name || `${u.first_name} ${u.last_name}`}</div>
                              <div style={{ fontSize: '12px', color: '#9e6575', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                            </div>
                            <div style={{ marginLeft: '12px', flexShrink: 0 }}>
                              {alreadyIn
                                ? <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 600, whiteSpace: 'nowrap' }}>✓ Inscrit</span>
                                : <button
                                    onClick={() => { addMember(u); setMemberSearch(''); setMemberResults([]); }}
                                    disabled={memberAddingId === u.id}
                                    style={{ padding: '5px 14px', background: memberAddingId === u.id ? '#cca7b0' : 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', borderRadius: '6px', cursor: memberAddingId === u.id ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
                                  >
                                    {memberAddingId === u.id ? '…' : '+ Inscrire'}
                                  </button>
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* Lien public */}
              <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid rgba(124,40,55,0.12)', padding: '28px' }}>
                <h3 style={{ color: '#2c0e16', marginBottom: '8px', fontWeight: '700' }}>🔗 Lien d'inscription publique</h3>
                <p style={{ color: '#7a4050', fontSize: '14px', marginBottom: '20px' }}>
                  Partagez ce lien — n'importe qui peut s'inscrire sans compte existant.
                </p>
                <div style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.18)', borderRadius: '10px', padding: '14px 16px', marginBottom: '14px', wordBreak: 'break-all', fontSize: '13px', color: '#555' }}>
                  {joinUrl}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(joinUrl); showToast('🔗 Lien copié !'); }}
                  style={{ width: '100%', padding: '12px', background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.25)', color: '#2c0e16', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                >
                  📋 Copier le lien
                </button>
                <button
                  onClick={() => window.open(joinUrl, '_blank')}
                  style={{ width: '100%', marginTop: '10px', padding: '12px', background: 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                >
                  👁️ Prévisualiser la page
                </button>
              </div>

              {/* Invitation manuelle */}
              <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid rgba(124,40,55,0.12)', padding: '28px' }}>
                <h3 style={{ color: '#2c0e16', marginBottom: '8px', fontWeight: '700' }}>✉️ Invitation manuelle</h3>
                <p style={{ color: '#7a4050', fontSize: '14px', marginBottom: '20px' }}>
                  Invitez directement une personne par email. Un compte est créé si elle n'en a pas.
                </p>
                <form onSubmit={handleInvite}>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={labelS}>Prénom *</label>
                    <input name="first_name" value={inviteForm.first_name} onChange={e => setInviteForm(f => ({...f, first_name: e.target.value}))} placeholder="Jean" required style={inputS} />
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={labelS}>Nom *</label>
                    <input name="last_name" value={inviteForm.last_name} onChange={e => setInviteForm(f => ({...f, last_name: e.target.value}))} placeholder="Dupont" required style={inputS} />
                  </div>
                  <div style={{ marginBottom: '18px' }}>
                    <label style={labelS}>Email *</label>
                    <input name="email" type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({...f, email: e.target.value}))} placeholder="jean.dupont@exemple.fr" required style={inputS} />
                  </div>
                  <div style={{ marginBottom: '18px' }}>
                    <label style={labelS}>Téléphone <span style={{ fontWeight: 400, color: '#9e6575' }}>(optionnel — pour recevoir un SMS)</span></label>
                    <input name="phone" type="tel" value={inviteForm.phone} onChange={e => setInviteForm(f => ({...f, phone: e.target.value}))} placeholder="+33 6 12 34 56 78" style={inputS} />
                  </div>
                  {inviteResult && inviteResult.success && (
                    <div style={{ background: '#f0fff4', border: '1px solid rgba(21,128,61,0.25)', borderRadius: '8px', padding: '12px', marginBottom: '14px', color: '#15803d', fontSize: '13px' }}>
                      ✅ {inviteResult.message}
                      {inviteResult.new_account && <div style={{ marginTop: '4px', color: '#7a4050' }}>📧 Un email avec ses identifiants lui a été envoyé.</div>}
                      {inviteResult.sms_sent && <div style={{ marginTop: '4px', color: '#7a4050' }}>📱 Un SMS d'invitation a également été envoyé.</div>}
                    </div>
                  )}
                  {inviteResult && !inviteResult.success && (
                    <div style={{ background: '#fdf0f2', border: '1px solid rgba(168,56,78,0.25)', borderRadius: '8px', padding: '12px', marginBottom: '14px', color: '#7c2837', fontSize: '13px' }}>
                      ❌ {inviteResult.message}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={inviteSubmitting}
                    style={{ width: '100%', padding: '13px', background: inviteSubmitting ? '#cca7b0' : 'linear-gradient(135deg,#7c2837,#a8384e)', color: '#fff', border: 'none', borderRadius: '10px', cursor: inviteSubmitting ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '15px' }}
                  >
                    {inviteSubmitting ? 'Envoi en cours…' : '📨 Envoyer l\'invitation'}
                  </button>
                </form>
              </div>
            </div>
          );
        })()}

        {/* ===== ONGLET STATISTIQUES ===== */}
        {activeTab === 'stats' && (
          <div className="event-responsive-grid event-dashboard-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid rgba(124,40,55,0.12)', padding: '24px' }}>
              <h3 style={{ color: '#2c0e16', marginBottom: '20px', fontWeight: '700' }}>📊 Inscriptions</h3>
              {[
                { label: 'Taux de remplissage', value: `${stats.fill_rate}%`, bar: stats.fill_rate, color: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)' },
                { label: 'Taux de paiement', value: `${stats.total_registrations > 0 ? Math.round(stats.paid_count / stats.total_registrations * 100) : 0}%`, bar: stats.total_registrations > 0 ? (stats.paid_count / stats.total_registrations * 100) : 0, color: '#15803d' },
                { label: 'Taux de présence', value: `${stats.total_registrations > 0 ? Math.round(stats.checked_in_count / stats.total_registrations * 100) : 0}%`, bar: stats.total_registrations > 0 ? (stats.checked_in_count / stats.total_registrations * 100) : 0, color: '#3b82f6' }
              ].map(item => (
                <div key={item.label} style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#9e6575', fontSize: '0.88rem' }}>{item.label}</span>
                    <span style={{ color: '#2c0e16', fontWeight: '700' }}>{item.value}</span>
                  </div>
                  <div style={{ background: '#fdf6f8', borderRadius: '6px', height: '8px' }}>
                    <div style={{ height: '100%', borderRadius: '6px', width: `${item.bar}%`, background: item.color.startsWith('linear') ? item.color : item.color, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid rgba(124,40,55,0.12)', padding: '24px' }}>
              <h3 style={{ color: '#2c0e16', marginBottom: '20px', fontWeight: '700' }}>💶 Finances</h3>
              {[
                { label: 'Revenus totaux', value: `${stats.total_revenue}€`, color: '#15803d' },
                { label: 'Paiements reçus', value: stats.paid_count, color: '#15803d' },
                { label: 'Paiements en attente', value: stats.unpaid_count, color: '#f59e0b' },
                { label: 'Prix moyen', value: `${stats.total_registrations > 0 ? (stats.total_revenue / stats.total_registrations).toFixed(2) : 0}€`, color: '#3b82f6' }
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(124,40,55,0.10)' }}>
                  <span style={{ color: '#9e6575' }}>{item.label}</span>
                  <span style={{ color: item.color, fontWeight: '700', fontSize: '1.1rem' }}>{item.value}</span>
                </div>
              ))}
            </div>

            {questionnaireFields.length > 0 && (
              <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid rgba(124,40,55,0.12)', padding: '24px' }}>
                <h3 style={{ color: '#2c0e16', marginBottom: '20px', fontWeight: '700' }}>📝 Questionnaire</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {questionnaireFields.map(field => {
                    const answeredCount = participants.filter(p => {
                      const value = p.custom_data?.[field.name];
                      return value != null && value !== '';
                    }).length;

                    return (
                      <div key={field.name} style={{ padding: '12px 14px', background: '#fdf6f8', borderRadius: '10px', border: '1px solid rgba(124,40,55,0.08)' }}>
                        <div style={{ color: '#2c0e16', fontWeight: 700, marginBottom: 4 }}>{field.label}</div>
                        <div style={{ color: '#7a4050', fontSize: '0.88rem' }}>{answeredCount} réponse(s) enregistrée(s)</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default EventDashboard;

